import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import RefreshIcon from '@mui/icons-material/Refresh';
import TuneIcon from '@mui/icons-material/Tune';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import VideocamIcon from '@mui/icons-material/Videocam';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { DataFreshness } from '../components/DataFreshness';
import { SiteLayout } from '../components/SiteLayout';
import {
  type MediaResponse,
  type PatternBriefResponse,
  resolveMediaUrl,
  useCompetitorWatch,
  useIndustryNews,
  useContentPatternDetail,
  useContentPatterns,
  useBriefJob,
  useGenerateBriefAsync,
  useGenerateBriefFromPattern,
  useGenerateCustomBrief,
  useGenerateImage,
  useGenerateVideo,
  useGenerateReel,
  useReelJob,
  useSynthesizePatterns,
  useGenerateCarousel,
  useGenerateClip,
  useClipJob,
  useLatestBrief,
} from '../api/marketing';
import { colors } from '../theme';
import { FEATURE_COMPETITOR_WATCH, FEATURE_LIFESTYLE_CATEGORY } from '../config/flags';

const MotionBox = motion.create(Box);

/**
 * Infer the brand-reference category from a brief so media gen anchors to the
 * right HY PRODUCT photo. This keys off what the post is SELLING (whiskey vs
 * cigar), NOT the scene/setting — HY is a spirits brand, so every one of its
 * posts should show the real bottle even when shot outdoors / in a lifestyle
 * setting. We DEFAULT to whiskey and only switch to cigar when the brief is
 * genuinely cigar-centric.
 *
 * History of this function (two bugs it now guards against):
 *  1. A naive `includes('cigar')` flipped whiskey briefs that merely mentioned
 *     a cigar ("no cigar in frame") into cigar mode → wrong/no anchor.
 *  2. Treating an outdoor/lifestyle SETTING as the lifestyle CATEGORY returned
 *     `lifestyle` → no anchor → text-only gen invented the bottle. A whiskey
 *     brief shot in a field is still a whiskey post. So there is no longer a
 *     lifestyle branch here — HY's own posts always anchor to a product.
 */
function inferBriefCategory(text: string): 'whiskey' | 'cigar' {
  const t = text.toLowerCase();
  // Negated/incidental mentions shouldn't trigger cigar mode.
  const negatedCigar = /\bno\s+cigar|without\s+(?:a\s+)?cigar|cigar[- ]free/.test(t);
  // Require a stronger signal: cigar named as the product/hero, or mentioned
  // more than once (a real cigar post repeats it), and not negated.
  const cigarMatches = (t.match(/\bcigars?\b/g) ?? []).length;
  const cigarHero =
    /hand-rolled\s+cigar|cigar\s+(?:hero|ritual)|^[^.]*\bcigars?\b.*\b(?:hero|focus|close-?up)/.test(
      t,
    );
  if (!negatedCigar && (cigarHero || cigarMatches >= 3)) return 'cigar';
  return 'whiskey';
}
// =================================================================
// SECTION HEADER
// =================================================================
function SectionTitle({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          color: colors.gold,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          mb: 0.5,
        }}
      >
        {eyebrow}
      </Typography>
      <Typography
        sx={{
          fontFamily: '"Playfair Display", Georgia, serif',
          fontSize: { xs: 24, md: 30 },
          fontWeight: 700,
          color: colors.textPrimary,
          lineHeight: 1.2,
        }}
      >
        {title}
      </Typography>
      {subtitle ? (
        <Typography sx={{ fontSize: 14, color: colors.textMuted, mt: 0.75 }}>{subtitle}</Typography>
      ) : null}
    </Box>
  );
}

function SectionCard({ children, sx }: { children: React.ReactNode; sx?: object }) {
  return (
    <Paper
      elevation={0}
      sx={{
        // Fill the grid cell so cards sharing a row stretch to equal height
        // (the parent grid uses alignItems: 'stretch').
        height: '100%',
        p: { xs: 2.5, md: 3 },
        border: `1px solid ${colors.border}`,
        borderRadius: 2,
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
}

// =================================================================
// HERO BRIEF (the headline card)
// =================================================================
/** Progress copy per pipeline stage, so the ~70s wait explains itself. */
const BRIEF_STAGE_LABELS: Record<string, string> = {
  patterns: 'Analyzing patterns…',
  writing: 'Reading trends & writing brief…',
  compliance: 'Compliance review…',
};

function HeroBrief() {
  const { data, isLoading, error } = useLatestBrief();
  const queryClient = useQueryClient();
  const generate = useGenerateBriefAsync();
  const [briefJobId, setBriefJobId] = useState<string | null>(null);
  const briefJob = useBriefJob(briefJobId);
  const generateImage = useGenerateImage();
  // Single 8s Veo clip — one continuous shot with one coherent audio track,
  // anchored to the real HY bottle. (The stitched 3-beat 24s path produced
  // jump-cut flow and 3 independent gibberish audio tracks, so we use the
  // single clip for a smooth, coherent reel.)
  const generateVideo = useGenerateVideo();
  // List of generated assets, newest first. Each click appends — nothing
  // is replaced, so the team can compare image + reel side by side.
  //
  // Persisted to localStorage so navigating away (e.g. to /marketing/patterns)
  // and back doesn't blow them away. Capped to the last 24 to keep storage
  // sane; older results scroll off. Tied to the current brief's timestamp so
  // a fresh brief gives the team a clean slate.
  const briefStamp = data?.recommendation?._generated_at ?? '';
  const storageKey = briefStamp ? `hy-marketing-media:${briefStamp}` : 'hy-marketing-media:current';

  const [generatedMedia, setGeneratedMedia] = useState<MediaResponse[]>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? (JSON.parse(raw) as MediaResponse[]) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      if (generatedMedia.length === 0) {
        localStorage.removeItem(storageKey);
      } else {
        localStorage.setItem(storageKey, JSON.stringify(generatedMedia.slice(0, 24)));
      }
    } catch {
      // Quota exceeded or storage disabled — silently ignore; in-memory state still works.
    }
  }, [storageKey, generatedMedia]);

  // When a new brief is generated, drop stale entries tied to old briefs.
  useEffect(() => {
    if (!briefStamp) return;
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (
          key &&
          key.startsWith('hy-marketing-media:') &&
          key !== storageKey &&
          key !== 'hy-marketing-media:current'
        ) {
          localStorage.removeItem(key);
        }
      }
    } catch {
      // ignore
    }
  }, [briefStamp, storageKey]);

  const removeMedia = (idx: number) =>
    setGeneratedMedia((prev) => prev.filter((_, i) => i !== idx));

  const handleGenerate = async () => {
    try {
      const job = await generate.mutateAsync();
      setBriefJobId(job.job_id);
    } catch {
      // generate.error renders below; nothing else to do here.
    }
  };

  // The job settles server-side; pull the fresh brief in when it lands.
  const jobStatus = briefJob.data?.status;
  useEffect(() => {
    if (jobStatus !== 'done') return;
    setGeneratedMedia([]); // a fresh brief invalidates the old visuals
    queryClient.invalidateQueries({ queryKey: ['marketing', 'briefs', 'latest'] });
    queryClient.invalidateQueries({ queryKey: ['marketing', 'patterns'] });
    setBriefJobId(null);
  }, [jobStatus, queryClient]);

  // Busy from the moment the job is requested until it settles. On error the
  // job id stays set so the alert renders; the button re-enables for a retry.
  const briefBusy =
    generate.isPending || (briefJobId !== null && jobStatus !== 'error' && jobStatus !== 'done');
  const briefStageLabel =
    (briefJob.data?.stage && BRIEF_STAGE_LABELS[briefJob.data.stage]) || 'Starting…';
  const briefJobError =
    briefJob.data?.status === 'error' ? (briefJob.data.error ?? 'Brief generation failed.') : null;

  if (isLoading) {
    return (
      <SectionCard sx={{ bgcolor: '#fefcf6' }}>
        <Skeleton variant="text" width={200} height={20} />
        <Skeleton variant="text" width="80%" height={40} sx={{ mt: 1 }} />
        <Skeleton variant="rectangular" height={120} sx={{ mt: 2, borderRadius: 1 }} />
      </SectionCard>
    );
  }

  if (error || !data) {
    return (
      <SectionCard sx={{ bgcolor: '#fefcf6' }}>
        <SectionTitle
          eyebrow="This week's AI brief"
          title="No brief yet"
          subtitle="Click 'Generate' to analyze this week's competitor signals and produce a dated HY whiskey content recommendation."
        />
        <Button
          variant="contained"
          startIcon={
            briefBusy ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />
          }
          onClick={handleGenerate}
          disabled={briefBusy}
          sx={{ mt: 1, bgcolor: colors.gold, '&:hover': { bgcolor: colors.goldDark } }}
        >
          {briefBusy ? briefStageLabel : "Generate this week's brief"}
        </Button>
        {generate.error || briefJobError ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {generate.error?.message || briefJobError}
          </Alert>
        ) : null}
      </SectionCard>
    );
  }

  const rec = data.recommendation;

  const fullCaption = [rec.caption_draft, (rec.hashtags ?? []).join(' ')]
    .filter(Boolean)
    .join('\n\n');

  const handleCopy = () => {
    if (!fullCaption) return;
    navigator.clipboard.writeText(fullCaption).catch(() => {});
  };

  const formatLabel = (rec.format ?? 'post').toString().trim();
  const formatBanner = formatLabel.toUpperCase();

  return (
    <SectionCard
      sx={{
        bgcolor: '#fefcf6',
        border: `1.5px solid ${colors.gold}40`,
        p: 0,
        overflow: 'hidden',
      }}
    >
      {/* FORMAT BANNER — visually announces what kind of post this is */}
      <Box
        sx={{
          px: { xs: 2.5, md: 3 },
          py: 1.25,
          bgcolor: colors.gold,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: 13, md: 14 },
            fontWeight: 800,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
          }}
        >
          {formatBanner}
        </Typography>
        <Typography
          sx={{
            fontSize: 12.5,
            fontWeight: 600,
            letterSpacing: '0.02em',
            whiteSpace: 'nowrap',
            opacity: 0.95,
          }}
        >
          Post date: {rec.post_day ?? '—'}
          {rec.post_time ? `, ${rec.post_time}` : ''}
        </Typography>
      </Box>

      {/* CARD BODY */}
      <Box sx={{ px: { xs: 2.5, md: 3 }, py: { xs: 2.5, md: 3 } }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: colors.gold,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            mb: 0.5,
          }}
        >
          Recommended for this week
        </Typography>
        <Typography
          sx={{
            fontFamily: '"Playfair Display", Georgia, serif',
            fontSize: { xs: 24, md: 32 },
            fontWeight: 700,
            color: colors.textPrimary,
            lineHeight: 1.2,
            mb: 0.5,
          }}
        >
          Your next post
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: colors.textMuted, mb: 2.5, maxWidth: 720 }}>
          Built from this week's competitor signals across 38 tracked accounts, translated into HY
          whiskey voice and pre-checked for TTB compliance. Edit anything below, copy when ready, or
          click Regenerate for a fresh take.
        </Typography>

        {/* TIER 3: theme / visual direction */}
        <LabeledBlock label="Theme & visual direction">
          <Typography sx={{ fontSize: 14, color: colors.textPrimary, lineHeight: 1.65 }}>
            {rec.theme}
          </Typography>
        </LabeledBlock>

        {/* TIER 4: caption */}
        {rec.caption_draft ? (
          <LabeledBlock label="Caption (ready to copy)" accent>
            <Typography
              sx={{
                fontSize: 14,
                color: colors.textPrimary,
                whiteSpace: 'pre-line',
                lineHeight: 1.65,
              }}
            >
              {rec.caption_draft}
            </Typography>
          </LabeledBlock>
        ) : null}

        {/* TIER 5: hashtags */}
        {(rec.hashtags ?? []).length > 0 ? (
          <Box sx={{ mb: 2.5 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: colors.textMuted,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                mb: 1,
              }}
            >
              Hashtags
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
              {(rec.hashtags ?? []).map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{ bgcolor: '#f3f4f6', color: colors.textSecondary, fontSize: 12 }}
                />
              ))}
            </Stack>
          </Box>
        ) : null}

        {/* TIER 6: actions */}
        <Stack direction="row" spacing={1.25} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
            onClick={handleCopy}
            sx={{
              bgcolor: colors.gold,
              color: '#fff',
              '&:hover': { bgcolor: colors.goldDark },
              textTransform: 'none',
              fontWeight: 700,
            }}
          >
            Copy caption + hashtags
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={
              generateImage.isPending ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <ImageIcon sx={{ fontSize: 16 }} />
              )
            }
            onClick={async () => {
              // Prefer the image-specific prompt when present (single hero
              // shot framing) — the video prompt has a BEAT 1/2/3/4 structure
              // that Flux interprets as a 4-panel collage. Fall back to
              // visual_direction for backwards compatibility.
              const imagePrompt = rec.image_visual_direction || rec.visual_direction;
              if (!imagePrompt) return;
              // Pick the HY product photo to anchor to. Whiskey-first; a passing
              // cigar mention must NOT flip this (see inferBriefCategory).
              const category = inferBriefCategory(`${rec.theme ?? ''} ${imagePrompt}`);
              const result = await generateImage.mutateAsync({
                visual_direction: imagePrompt,
                aspect_ratio: '1:1',
                use_brand_reference: true,
                category,
                // Force the composite path for whiskey hero stills — real HY
                // bottle pasted into a bottle-free scene, so no duplicate/invented
                // bottle and no garbled label. Backend falls back to two-pass for
                // non-whiskey or when no reference resolves.
                composite: true,
              });
              setGeneratedMedia((prev) => [result, ...prev]);
            }}
            disabled={generateImage.isPending || generateVideo.isPending || !rec.visual_direction}
            sx={{
              borderColor: colors.gold,
              color: colors.goldDark,
              textTransform: 'none',
              fontWeight: 700,
            }}
          >
            {generateImage.isPending ? 'Generating image…' : 'Generate image'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={
              generateVideo.isPending ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <VideocamIcon sx={{ fontSize: 16 }} />
              )
            }
            onClick={async () => {
              if (!rec.visual_direction) return;
              // Single 8s clip, anchored to the real HY bottle. Same
              // whiskey-first inference as the image button.
              const category = inferBriefCategory(`${rec.theme ?? ''} ${rec.visual_direction}`);
              const result = await generateVideo.mutateAsync({
                visual_direction: rec.visual_direction,
                aspect_ratio: '9:16',
                duration_sec: 8,
                with_audio: true,
                use_brand_reference: true,
                category,
              });
              setGeneratedMedia((prev) => [result, ...prev]);
            }}
            disabled={generateVideo.isPending || generateImage.isPending || !rec.visual_direction}
            sx={{
              borderColor: colors.gold,
              color: colors.goldDark,
              textTransform: 'none',
              fontWeight: 700,
            }}
          >
            {generateVideo.isPending ? 'Generating reel…' : 'Generate reel'}
          </Button>
          <Button
            variant="text"
            size="small"
            startIcon={
              briefBusy ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />
            }
            onClick={handleGenerate}
            disabled={briefBusy}
            sx={{
              color: colors.textMuted,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {briefBusy ? briefStageLabel : 'Regenerate brief'}
          </Button>
        </Stack>

        {/* TIER 7: generated visual (if any) */}
        {generateImage.error || generateVideo.error || generate.error || briefJobError ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {generateImage.error?.message ||
              generateVideo.error?.message ||
              generate.error?.message ||
              briefJobError}
          </Alert>
        ) : null}

        {generatedMedia.length > 0 ? (
          <Box sx={{ mt: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: colors.textMuted,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                Generated assets ({generatedMedia.length})
              </Typography>
              {generatedMedia.length > 1 ? (
                <Button
                  size="small"
                  onClick={() => setGeneratedMedia([])}
                  sx={{
                    fontSize: 11,
                    color: colors.textMuted,
                    textTransform: 'none',
                    minWidth: 'auto',
                  }}
                >
                  Clear all
                </Button>
              ) : null}
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                },
                gap: 2,
              }}
            >
              {generatedMedia.map((m, idx) => (
                <Paper
                  key={`${m.generated_at}-${idx}`}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    bgcolor: '#fff',
                    border: `1px solid ${colors.border}`,
                    borderRadius: 1,
                    position: 'relative',
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Chip
                      label={m.kind === 'image' ? 'IMAGE' : 'REEL'}
                      size="small"
                      icon={
                        m.kind === 'image' ? (
                          <ImageIcon sx={{ fontSize: 13 }} />
                        ) : (
                          <VideocamIcon sx={{ fontSize: 13 }} />
                        )
                      }
                      sx={{
                        bgcolor: `${colors.gold}15`,
                        color: colors.goldDark,
                        fontWeight: 700,
                        fontSize: 10,
                        height: 22,
                        '& .MuiChip-icon': { color: colors.goldDark },
                      }}
                    />
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Button
                        component="a"
                        href={resolveMediaUrl(m.url)}
                        download={`hy-${m.kind}-${new Date(m.generated_at)
                          .toISOString()
                          .replace(/[:.]/g, '-')
                          .slice(0, 19)}.${m.kind === 'image' ? 'png' : 'mp4'}`}
                        size="small"
                        sx={{
                          minWidth: 'auto',
                          px: 0.75,
                          py: 0,
                          color: colors.textMuted,
                          '&:hover': { color: colors.goldDark, bgcolor: 'transparent' },
                        }}
                        aria-label="Download"
                        title="Download"
                      >
                        <DownloadIcon sx={{ fontSize: 16 }} />
                      </Button>
                      <Button
                        onClick={() => removeMedia(idx)}
                        size="small"
                        sx={{
                          minWidth: 'auto',
                          px: 0.75,
                          py: 0,
                          fontSize: 14,
                          color: colors.textMuted,
                          '&:hover': { color: colors.error, bgcolor: 'transparent' },
                        }}
                        aria-label="Dismiss"
                        title="Dismiss"
                      >
                        ✕
                      </Button>
                    </Stack>
                  </Stack>
                  {m.kind === 'image' ? (
                    <Box
                      component="img"
                      src={resolveMediaUrl(m.url)}
                      alt="Generated"
                      sx={{
                        width: '100%',
                        borderRadius: 1,
                        display: 'block',
                        aspectRatio: '1 / 1',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <Box
                      component="video"
                      src={resolveMediaUrl(m.url)}
                      controls
                      sx={{
                        width: '100%',
                        borderRadius: 1,
                        display: 'block',
                        aspectRatio: '9 / 16',
                        objectFit: 'cover',
                        bgcolor: '#000',
                      }}
                    />
                  )}
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      color: colors.textMuted,
                      mt: 1,
                      letterSpacing: '0.02em',
                    }}
                  >
                    {new Date(m.generated_at).toLocaleTimeString([], {
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        ) : null}
      </Box>
    </SectionCard>
  );
}

/** Reusable labeled block used for Theme and Caption sections. */
function LabeledBlock({
  label,
  children,
  accent = false,
}: {
  label: string;
  children: React.ReactNode;
  accent?: boolean;
}) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          color: accent ? colors.goldDark : colors.textMuted,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          mb: 1,
        }}
      >
        {label}
      </Typography>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          bgcolor: '#fff',
          border: `1px solid ${colors.border}`,
          borderLeft: `3px solid ${accent ? colors.gold : colors.borderLight}`,
          borderRadius: 1,
        }}
      >
        {children}
      </Paper>
    </Box>
  );
}

// =================================================================
// PATTERN INSIGHTS (what we learned from the data)
// =================================================================
// =================================================================
// CONTENT PATTERN MAP — pick a category×format pattern, generate a
// brief from it, edit the prompts, then fire image or video creation.
// =================================================================

const CATEGORY_COLOR: Record<string, string> = {
  whiskey: colors.gold,
  cigar: '#8b5cf6',
  lifestyle: '#0ea5e9',
};

function BriefGeneratorPanel({
  patternId,
  category,
  postType,
  initialBrief,
}: {
  patternId: number | null;
  category: string;
  postType: string;
  initialBrief?: PatternBriefResponse | null;
}) {
  const { data: detail } = useContentPatternDetail(patternId);
  const generateBrief = useGenerateBriefFromPattern();
  const generateImage = useGenerateImage();
  const generateReel = useGenerateReel();
  const generateHighgsfield = useGenerateClip();
  const [reelJobId, setReelJobId] = useState<string | null>(null);
  const reelJob = useReelJob(reelJobId);
  const [clipJobId, setClipJobId] = useState<string | null>(null);
  const clipJob = useClipJob(clipJobId);
  const generateCarousel = useGenerateCarousel();
  const [carouselSlides, setCarouselSlides] = useState<string[]>([]);

  const [brief, setBrief] = useState<PatternBriefResponse | null>(initialBrief ?? null);
  const [videoPrompt, setVideoPrompt] = useState(
    (initialBrief?.brief.visual_direction as string | undefined) ?? '',
  );
  const [imagePrompt, setImagePrompt] = useState(
    (initialBrief?.brief.image_visual_direction as string | undefined) ??
      (initialBrief?.brief.visual_direction as string | undefined) ??
      '',
  );
  const [generatedMedia, setGeneratedMedia] = useState<{
    kind: 'image' | 'video';
    url: string;
  } | null>(null);

  const accent = CATEGORY_COLOR[category] ?? colors.gold;
  const isLifestyle = category === 'lifestyle';

  useEffect(() => {
    if (!initialBrief) return;
    setBrief(initialBrief);
    setVideoPrompt((initialBrief.brief.visual_direction as string | undefined) ?? '');
    setImagePrompt(
      (initialBrief.brief.image_visual_direction as string | undefined) ??
        (initialBrief.brief.visual_direction as string | undefined) ??
        '',
    );
    setGeneratedMedia(null);
  }, [initialBrief]);

  useEffect(() => {
    if (reelJob.data?.status === 'done' && reelJob.data.url) {
      setGeneratedMedia({ kind: 'video', url: resolveMediaUrl(reelJob.data.url) });
      setReelJobId(null);
    }
  }, [reelJob.data]);

  useEffect(() => {
    if (clipJob.data?.status === 'done' && clipJob.data.url) {
      setGeneratedMedia({ kind: 'video', url: resolveMediaUrl(clipJob.data.url) });
      setClipJobId(null);
    } else if (clipJob.data?.status === 'error') {
      setClipJobId(null);
    }
  }, [clipJob.data]);

  async function handleGenerateBrief() {
    if (patternId === null) return;
    const result = await generateBrief.mutateAsync(patternId);
    setBrief(result);
    setVideoPrompt((result.brief.visual_direction as string | undefined) ?? '');
    setImagePrompt(
      (result.brief.image_visual_direction as string | undefined) ??
        (result.brief.visual_direction as string | undefined) ??
        '',
    );
    setGeneratedMedia(null);
  }

  async function handleGenerateVideo() {
    const res = await generateReel.mutateAsync({
      visual_direction: videoPrompt,
      n_clips: 1,
      aspect_ratio: '9:16',
      with_audio: true,
      use_brand_reference: !isLifestyle,
      category: category as 'whiskey' | 'cigar' | 'lifestyle',
    });
    setReelJobId(res.job_id);
  }

  async function handleGenerateImage() {
    const res = await generateImage.mutateAsync({
      visual_direction: imagePrompt,
      aspect_ratio: '4:5',
      use_brand_reference: !isLifestyle,
      category: category as 'whiskey' | 'cigar' | 'lifestyle',
    });
    setGeneratedMedia({ kind: 'image', url: resolveMediaUrl(res.url) });
  }

  async function handleGenerateCarousel() {
    // Pull per-slide prompts from the brief; fall back to the image prompt as a
    // single slide if the brief didn't produce them.
    const briefSlides =
      (brief?.brief.slide_prompts as { prompt: string }[] | undefined)?.map((s) => s.prompt) ?? [];
    const prompts = briefSlides.length ? briefSlides : imagePrompt ? [imagePrompt] : [];
    if (!prompts.length) return;
    const res = await generateCarousel.mutateAsync({
      slide_prompts: prompts,
      aspect_ratio: '4:5',
      use_brand_reference: !isLifestyle,
      category: category as 'whiskey' | 'cigar' | 'lifestyle',
    });
    setCarouselSlides(res.slides.map((s) => resolveMediaUrl(s.url)));
  }

  async function handleGenerateHighgsfield() {
    setGeneratedMedia(null);
    const res = await generateHighgsfield.mutateAsync({
      visual_direction: videoPrompt,
      aspect_ratio: '9:16',
      duration_sec: 6,
    });
    setClipJobId(res.job_id);
  }

  return (
    <Box
      sx={{
        mt: 2,
        p: 2.5,
        border: `1px solid ${accent}40`,
        borderRadius: 2,
        bgcolor: `${accent}06`,
      }}
    >
      {/* Synthesis detail */}
      {detail?.synthesis && (
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: colors.textMuted,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              mb: 0.75,
            }}
          >
            Pattern synthesis
          </Typography>
          <Typography sx={{ fontSize: 13, color: colors.textSecondary, lineHeight: 1.6 }}>
            {detail.synthesis}
          </Typography>
        </Box>
      )}

      {/* Generate brief button */}
      {!brief && (
        <Button
          variant="contained"
          size="small"
          startIcon={
            generateBrief.isPending ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <AutoAwesomeIcon sx={{ fontSize: 16 }} />
            )
          }
          disabled={generateBrief.isPending}
          onClick={handleGenerateBrief}
          sx={{
            bgcolor: accent,
            '&:hover': { bgcolor: accent, filter: 'brightness(0.88)' },
            fontSize: 13,
            fontWeight: 700,
            px: 2.5,
          }}
        >
          {generateBrief.isPending ? 'Generating brief…' : `Generate ${postType} brief`}
        </Button>
      )}

      {generateBrief.isError && (
        <Alert severity="error" sx={{ mt: 1, fontSize: 12 }}>
          {(generateBrief.error as Error).message}
        </Alert>
      )}

      {/* Brief output + editable prompts */}
      {brief && (
        <Stack spacing={2}>
          {/* Compliance badge */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={brief.compliance.verdict?.toString().toUpperCase() ?? 'REVIEWED'}
              size="small"
              sx={{
                bgcolor: brief.compliance.verdict === 'compliant' ? '#16a34a22' : '#ef444422',
                color: brief.compliance.verdict === 'compliant' ? '#16a34a' : '#ef4444',
                fontWeight: 700,
                fontSize: 11,
              }}
            />
            {brief.revised && (
              <Typography sx={{ fontSize: 11, color: colors.textMuted }}>
                (copy was revised for compliance)
              </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
              onClick={() => {
                setBrief(null);
                setGeneratedMedia(null);
              }}
              sx={{ fontSize: 12, color: colors.textMuted }}
            >
              Regenerate
            </Button>
          </Stack>

          <Divider sx={{ borderColor: colors.borderLight }} />

          {/* Theme + caption */}
          {brief.brief.theme && (
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: colors.textMuted,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  mb: 0.5,
                }}
              >
                Theme
              </Typography>
              <Typography sx={{ fontSize: 13, color: colors.textPrimary }}>
                {brief.brief.theme as string}
              </Typography>
            </Box>
          )}

          {/* Video prompt */}
          <Box>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.75 }}>
              <VideocamIcon sx={{ fontSize: 14, color: colors.textMuted }} />
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: colors.textMuted,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                Video prompt — edit before generating
              </Typography>
            </Stack>
            <TextField
              multiline
              minRows={3}
              fullWidth
              value={videoPrompt}
              onChange={(e) => setVideoPrompt(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: 13,
                  bgcolor: colors.surfaceSubtle,
                  '& fieldset': { borderColor: colors.border },
                  '&:hover fieldset': { borderColor: accent },
                  '&.Mui-focused fieldset': { borderColor: accent },
                },
              }}
            />
          </Box>

          {/* Image prompt */}
          <Box>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.75 }}>
              <ImageIcon sx={{ fontSize: 14, color: colors.textMuted }} />
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: colors.textMuted,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                Image prompt — edit before generating
              </Typography>
            </Stack>
            <TextField
              multiline
              minRows={2}
              fullWidth
              value={imagePrompt}
              onChange={(e) => setImagePrompt(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: 13,
                  bgcolor: colors.surfaceSubtle,
                  '& fieldset': { borderColor: colors.border },
                  '&:hover fieldset': { borderColor: accent },
                  '&.Mui-focused fieldset': { borderColor: accent },
                },
              }}
            />
          </Box>

          {/* Generate buttons — gated by the chosen format:
              reel → Reel only · image → Image + Reel · carousel → Carousel */}
          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            {/* Carousel → multi-slide image generation */}
            {postType === 'carousel' ? (
              <Button
                variant="contained"
                size="small"
                startIcon={
                  generateCarousel.isPending ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <ImageIcon sx={{ fontSize: 16 }} />
                  )
                }
                disabled={generateCarousel.isPending}
                onClick={handleGenerateCarousel}
                sx={{
                  bgcolor: accent,
                  '&:hover': { bgcolor: accent, filter: 'brightness(0.88)' },
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                {generateCarousel.isPending ? 'Generating slides…' : 'Generate Carousel'}
              </Button>
            ) : null}

            {/* Image button — shown for image format */}
            {postType === 'image' ? (
              <Button
                variant="contained"
                size="small"
                startIcon={
                  generateImage.isPending ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <ImageIcon sx={{ fontSize: 16 }} />
                  )
                }
                disabled={generateImage.isPending || !imagePrompt}
                onClick={handleGenerateImage}
                sx={{
                  bgcolor: accent,
                  '&:hover': { bgcolor: accent, filter: 'brightness(0.88)' },
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                {generateImage.isPending ? 'Generating image…' : 'Generate Image'}
              </Button>
            ) : null}

            {/* Reel button — shown for reel + image (image stills can also animate) */}
            {postType === 'reel' || postType === 'image' ? (
              <Button
                variant={postType === 'reel' ? 'contained' : 'outlined'}
                size="small"
                startIcon={
                  generateReel.isPending || reelJobId ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <VideocamIcon sx={{ fontSize: 16 }} />
                  )
                }
                disabled={generateReel.isPending || !!reelJobId || !videoPrompt}
                onClick={handleGenerateVideo}
                sx={
                  postType === 'reel'
                    ? {
                        bgcolor: accent,
                        '&:hover': { bgcolor: accent, filter: 'brightness(0.88)' },
                        fontSize: 13,
                        fontWeight: 700,
                      }
                    : { borderColor: accent, color: accent, fontSize: 13, fontWeight: 700 }
                }
              >
                {generateReel.isPending
                  ? 'Queuing…'
                  : reelJobId
                    ? `Generating${reelJob.data?.status === 'running' ? ' (stitching)' : ''}…`
                    : 'Generate Reel (8s)'}
              </Button>
            ) : null}

            {/* Higgsfield clip — secondary video option for reel/image */}
            {postType === 'reel' || postType === 'image' ? (
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  generateHighgsfield.isPending || clipJobId ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <VideocamIcon sx={{ fontSize: 16 }} />
                  )
                }
                disabled={generateHighgsfield.isPending || !!clipJobId || !videoPrompt}
                onClick={handleGenerateHighgsfield}
                sx={{ borderColor: '#6b6b6b', color: '#6b6b6b', fontSize: 13, fontWeight: 700 }}
              >
                {generateHighgsfield.isPending || clipJobId
                  ? 'Generating (Higgsfield)…'
                  : 'Generate Clip (Higgsfield)'}
              </Button>
            ) : null}
          </Stack>

          {/* Clip job error */}
          {clipJob.data?.status === 'error' && (
            <Box
              sx={{ mt: 1, p: 1, bgcolor: '#2a1515', borderRadius: 1, fontSize: 12, color: '#f88' }}
            >
              Clip generation failed: {clipJob.data.error ?? 'unknown error'}
            </Box>
          )}

          {/* Reel job error */}
          {reelJob.data?.status === 'error' && (
            <Box
              sx={{ mt: 1, p: 1, bgcolor: '#2a1515', borderRadius: 1, fontSize: 12, color: '#f88' }}
            >
              Reel failed: {reelJob.data.error}
            </Box>
          )}

          {/* Generated media preview */}
          {generatedMedia && (
            <Box
              sx={{
                mt: 1,
                borderRadius: 1.5,
                overflow: 'hidden',
                border: `1px solid ${colors.border}`,
                maxWidth: 320,
              }}
            >
              {generatedMedia.kind === 'video' ? (
                <video
                  src={generatedMedia.url}
                  controls
                  autoPlay
                  muted
                  style={{ width: '100%', display: 'block' }}
                />
              ) : (
                <img
                  src={generatedMedia.url}
                  alt="Generated"
                  style={{ width: '100%', display: 'block' }}
                />
              )}
            </Box>
          )}

          {/* Carousel slides — horizontal strip of generated images */}
          {carouselSlides.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: colors.textMuted,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  mb: 1,
                }}
              >
                Carousel · {carouselSlides.length} slides
              </Typography>
              <Stack direction="row" spacing={1.5} sx={{ overflowX: 'auto', pb: 1 }}>
                {carouselSlides.map((url, i) => (
                  <Box
                    key={url}
                    sx={{
                      position: 'relative',
                      flex: '0 0 auto',
                      width: 180,
                      borderRadius: 1.5,
                      overflow: 'hidden',
                      border: `1px solid ${colors.border}`,
                    }}
                  >
                    <img
                      src={url}
                      alt={`Slide ${i + 1}`}
                      style={{ width: '100%', display: 'block' }}
                    />
                    <Chip
                      label={i + 1}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 6,
                        left: 6,
                        height: 20,
                        fontSize: 11,
                        fontWeight: 700,
                        bgcolor: 'rgba(0,0,0,0.6)',
                        color: '#fff',
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
}

// Chips are rendered from this constant, NOT from the API response — filtering
// lifestyle out server-side does not remove the button. The flag has to be
// applied here too.
const CATEGORIES = (
  FEATURE_LIFESTYLE_CATEGORY ? ['whiskey', 'cigar', 'lifestyle'] : ['whiskey', 'cigar']
) as readonly ('whiskey' | 'cigar' | 'lifestyle')[];
const FORMATS = ['reel', 'image', 'carousel'] as const;

function ContentPatternMap() {
  const { data: patterns, isLoading, error } = useContentPatterns();
  const generateCustom = useGenerateCustomBrief();
  const synthesize = useSynthesizePatterns();

  const [selectedCategory, setSelectedCategory] = useState<string>('whiskey');
  const [selectedFormat, setSelectedFormat] = useState<string>('reel');
  const [customFocus, setCustomFocus] = useState('');
  const [generatedBrief, setGeneratedBrief] = useState<PatternBriefResponse | null>(null);

  const list = patterns ?? [];

  // Find the pre-synthesized pattern for the current selection (for context display)
  const matchedPattern =
    list.find((p) => p.category === selectedCategory && p.post_type === selectedFormat) ?? null;

  // Reset brief when the user changes the selection
  function handleCategoryChange(cat: string) {
    setSelectedCategory(cat);
    setGeneratedBrief(null);
  }
  function handleFormatChange(fmt: string) {
    setSelectedFormat(fmt);
    setGeneratedBrief(null);
  }

  async function handleGenerate() {
    const result = await generateCustom.mutateAsync({
      category: selectedCategory,
      post_type: selectedFormat,
      custom_focus: customFocus.trim() || undefined,
    });
    setGeneratedBrief(result);
  }

  async function handleSynthesize() {
    // Re-runs the pattern engine across all groups — slow (~1-2 min) and costs
    // model calls, so confirm before firing.
    const ok = window.confirm(
      'Re-analyze the latest data and refresh all pattern insights? This takes 1–2 minutes.',
    );
    if (!ok) return;
    await synthesize.mutateAsync();
  }

  const accentColor = CATEGORY_COLOR[selectedCategory] ?? colors.gold;

  return (
    <SectionCard>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={1.5}
        sx={{ mb: 1 }}
      >
        <SectionTitle
          eyebrow="Pattern map"
          title="Generate from Patterns"
          subtitle="Patterns extracted from 38 competitor and benchmark accounts. Pick a category × format to generate an HY whiskey brief from what's winning in your peer set."
        />
        <Button
          size="small"
          startIcon={
            synthesize.isPending ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <RefreshIcon sx={{ fontSize: 16 }} />
            )
          }
          onClick={handleSynthesize}
          disabled={synthesize.isPending}
          sx={{
            flexShrink: 0,
            mt: 0.5,
            color: colors.textMuted,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 12.5,
            whiteSpace: 'nowrap',
          }}
        >
          {synthesize.isPending ? 'Refreshing…' : 'Refresh patterns'}
        </Button>
      </Stack>

      {synthesize.isSuccess && !synthesize.isPending ? (
        <Alert severity="success" sx={{ mb: 2, fontSize: 13 }}>
          Patterns refreshed from the latest data.
        </Alert>
      ) : null}
      {synthesize.isError ? (
        <Alert severity="error" sx={{ mb: 2, fontSize: 13 }}>
          Refresh failed: {(synthesize.error as Error).message}
        </Alert>
      ) : null}

      {isLoading ? (
        <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 1.5 }} />
      ) : error ? (
        <Alert severity="error">Failed to load patterns: {(error as Error).message}</Alert>
      ) : (
        <Stack spacing={2.5}>
          {/* ── Step 1: Category ── */}
          <Box>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: colors.textMuted,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                mb: 1,
              }}
            >
              Category
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {CATEGORIES.map((cat) => {
                const accent = CATEGORY_COLOR[cat] ?? colors.gold;
                const active = selectedCategory === cat;
                return (
                  <Chip
                    key={cat}
                    label={cat.charAt(0).toUpperCase() + cat.slice(1)}
                    onClick={() => handleCategoryChange(cat)}
                    sx={{
                      fontWeight: 700,
                      fontSize: 12,
                      bgcolor: active ? `${accent}22` : 'transparent',
                      color: active ? accent : colors.textSecondary,
                      border: `1.5px solid ${active ? accent : colors.border}`,
                      cursor: 'pointer',
                      textTransform: 'capitalize',
                      '&:hover': { bgcolor: `${accent}14`, borderColor: accent },
                    }}
                  />
                );
              })}
            </Stack>
          </Box>

          {/* ── Step 2: Format ── */}
          <Box>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: colors.textMuted,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                mb: 1,
              }}
            >
              Format
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {FORMATS.map((fmt) => {
                const active = selectedFormat === fmt;
                return (
                  <Chip
                    key={fmt}
                    label={fmt.charAt(0).toUpperCase() + fmt.slice(1)}
                    onClick={() => handleFormatChange(fmt)}
                    sx={{
                      fontWeight: 700,
                      fontSize: 12,
                      bgcolor: active ? `${accentColor}22` : 'transparent',
                      color: active ? accentColor : colors.textSecondary,
                      border: `1.5px solid ${active ? accentColor : colors.border}`,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: `${accentColor}14`, borderColor: accentColor },
                    }}
                  />
                );
              })}
            </Stack>
          </Box>

          {/* ── Step 3: Custom focus ── */}
          <Box>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: colors.textMuted,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                mb: 1,
              }}
            >
              Custom Focus{' '}
              <Typography
                component="span"
                sx={{ fontSize: 11, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}
              >
                (optional)
              </Typography>
            </Typography>
            <TextField
              placeholder="e.g. 'heritage gifting', 'behind-the-scenes craft', 'military tribute'"
              value={customFocus}
              onChange={(e) => setCustomFocus(e.target.value)}
              size="small"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: 13,
                  '& fieldset': { borderColor: colors.border },
                  '&:hover fieldset': { borderColor: accentColor },
                  '&.Mui-focused fieldset': { borderColor: accentColor },
                },
              }}
            />
          </Box>

          {/* ── Pattern insight ── */}
          <Collapse in={matchedPattern !== null && matchedPattern?.synthesis != null} unmountOnExit>
            {matchedPattern?.synthesis && (
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: `${accentColor}0a`,
                  border: `1px solid ${accentColor}30`,
                  borderLeft: `3px solid ${accentColor}`,
                  borderRadius: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: accentColor,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    mb: 0.5,
                  }}
                >
                  Pattern insight — {selectedCategory} × {selectedFormat}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: colors.textSecondary, lineHeight: 1.6 }}>
                  {matchedPattern.synthesis}
                </Typography>
              </Box>
            )}
          </Collapse>

          {/* ── No pattern yet warning ── */}
          {list.length > 0 && !matchedPattern && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 12, color: colors.textMuted }}>
                No synthesized pattern for {selectedCategory} × {selectedFormat} yet.
              </Typography>
              <RouterLink to="/marketing/patterns" style={{ textDecoration: 'none' }}>
                <Typography sx={{ fontSize: 12, color: colors.gold, fontWeight: 600 }}>
                  Run synthesis →
                </Typography>
              </RouterLink>
            </Box>
          )}

          {/* ── Generate button ── */}
          <Box>
            <Button
              variant="contained"
              size="small"
              startIcon={
                generateCustom.isPending ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                )
              }
              disabled={generateCustom.isPending || (!matchedPattern && list.length > 0)}
              onClick={handleGenerate}
              sx={{
                bgcolor: accentColor,
                '&:hover': { bgcolor: accentColor, filter: 'brightness(0.88)' },
                '&.Mui-disabled': { bgcolor: `${accentColor}40`, color: '#fff8' },
                fontSize: 13,
                fontWeight: 700,
                px: 2.5,
              }}
            >
              {generateCustom.isPending
                ? 'Generating brief…'
                : `Generate HY ${selectedCategory} brief`}
            </Button>
            {generateCustom.isError && (
              <Typography sx={{ fontSize: 12, color: '#f44', mt: 0.75 }}>
                {(generateCustom.error as Error).message}
              </Typography>
            )}
          </Box>

          {/* ── Brief result panel ── */}
          <Collapse in={generatedBrief !== null} unmountOnExit>
            {generatedBrief && (
              <BriefGeneratorPanel
                key={`${generatedBrief.pattern_id}-${generatedBrief.generated_at}`}
                patternId={null}
                category={generatedBrief.category}
                postType={generatedBrief.post_type}
                initialBrief={generatedBrief}
              />
            )}
          </Collapse>
        </Stack>
      )}
    </SectionCard>
  );
}

// =================================================================
// INDUSTRY NEWS LINK — compact tile showing this week's spirits-news
// takeaway, linking to the full digest at /marketing/news.
// =================================================================
function IndustryNewsLink() {
  const { data, isLoading } = useIndustryNews();
  const takeaway = data?.headline_takeaway ?? '';

  return (
    <SectionCard sx={{ display: 'flex', flexDirection: 'column' }}>
      <SectionTitle
        eyebrow="Industry news"
        title="This week in spirits"
        subtitle="The day's drinks-industry press, curated down to what matters for HY."
      />
      {isLoading ? (
        <Skeleton variant="rectangular" height={88} sx={{ borderRadius: 1 }} />
      ) : (
        <Stack spacing={2} sx={{ flex: 1, justifyContent: 'space-between' }}>
          <Typography
            sx={{
              fontSize: 14.5,
              color: colors.textPrimary,
              lineHeight: 1.55,
              fontStyle: takeaway ? 'normal' : 'italic',
            }}
          >
            {takeaway || 'No digest available yet — open to refresh.'}
          </Typography>
          <Box>
            <RouterLink to="/marketing/news" style={{ textDecoration: 'none' }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 2.25,
                  py: 1.1,
                  borderRadius: '10px',
                  bgcolor: colors.gold,
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  boxShadow: `0 4px 14px ${colors.gold}35`,
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: colors.goldDark,
                    transform: 'translateY(-1px)',
                    boxShadow: `0 6px 18px ${colors.gold}50`,
                  },
                }}
              >
                View digest
                <ArrowForwardIcon sx={{ fontSize: 17 }} />
              </Box>
            </RouterLink>
          </Box>
        </Stack>
      )}
    </SectionCard>
  );
}

// =================================================================
// BLOG LINK — tile that opens the Toast & Light blog generator/reader.
// =================================================================
function BlogLink() {
  return (
    <SectionCard sx={{ display: 'flex', flexDirection: 'column' }}>
      <SectionTitle
        eyebrow="Toast & Light"
        title="The HY blog"
        subtitle="Generate an on-brand article and preview it as readers would — like, comment, shop."
      />
      <Stack spacing={2} sx={{ flex: 1, justifyContent: 'flex-end' }}>
        <Box>
          <RouterLink to="/marketing/blog" style={{ textDecoration: 'none' }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 2.25,
                py: 1.1,
                borderRadius: '10px',
                bgcolor: colors.gold,
                color: '#fff',
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: '0.02em',
                boxShadow: `0 4px 14px ${colors.gold}35`,
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: colors.goldDark,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 6px 18px ${colors.gold}50`,
                },
              }}
            >
              Open the blog
              <ArrowForwardIcon sx={{ fontSize: 17 }} />
            </Box>
          </RouterLink>
        </Box>
      </Stack>
    </SectionCard>
  );
}

// =================================================================
// COMPETITOR LANDSCAPE LINK — compact summary tile that navigates
// to the dedicated /marketing/competitors page (tabs for whiskey,
// cigar, lifestyle).
// =================================================================
function CompetitorLandscapeLink() {
  const { data, isLoading } = useCompetitorWatch('all');
  const totalBrands = data?.rows.length ?? 0;

  return (
    <SectionCard sx={{ display: 'flex', flexDirection: 'column' }}>
      <SectionTitle
        eyebrow="Competitive landscape"
        title="HY vs The Field"
        subtitle="Where HY sits in whiskey, cigar, and lifestyle peer sets."
      />
      {isLoading ? (
        <Skeleton variant="rectangular" height={88} sx={{ borderRadius: 1 }} />
      ) : (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ flex: 1 }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontSize: { xs: 36, md: 44 },
                fontWeight: 700,
                color: colors.gold,
                lineHeight: 1,
              }}
            >
              {totalBrands}
            </Typography>
            <Typography sx={{ fontSize: 13, color: colors.textSecondary, mt: 0.75 }}>
              competitor brands tracked across categories
            </Typography>
            <Box sx={{ mt: 1 }}>
              <DataFreshness
                dataThrough={data?.data_through}
                dataIngestedAt={data?.data_ingested_at}
                dataSource={data?.data_source}
                variant="compact"
              />
            </Box>
          </Box>
          <RouterLink to="/marketing/competitors" style={{ textDecoration: 'none' }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 2.25,
                py: 1.1,
                borderRadius: '10px',
                bgcolor: colors.gold,
                color: '#fff',
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: '0.02em',
                boxShadow: `0 4px 14px ${colors.gold}35`,
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: colors.goldDark,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 6px 18px ${colors.gold}50`,
                },
              }}
            >
              View landscape
              <ArrowForwardIcon sx={{ fontSize: 17 }} />
            </Box>
          </RouterLink>
        </Stack>
      )}
    </SectionCard>
  );
}

// =================================================================
// PATTERN BUILDER LINK — entry tile that navigates to the dedicated
// /marketing/pattern-builder page for selecting visual parameters
// and generating a tailored HY brief.
// =================================================================
function PatternBuilderLink() {
  return (
    <SectionCard sx={{ display: 'flex', flexDirection: 'column' }}>
      <SectionTitle
        eyebrow="Visual pattern builder"
        title="Build from the Data"
        subtitle="Pick visual parameters from competitor analysis to generate a tailored HY brief."
      />
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ flex: 1 }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontSize: { xs: 36, md: 44 },
              fontWeight: 700,
              color: colors.gold,
              lineHeight: 1,
            }}
          >
            14
          </Typography>
          <Typography sx={{ fontSize: 13, color: colors.textSecondary, mt: 0.75 }}>
            visual dimensions analyzed across categories
          </Typography>
        </Box>
        <RouterLink to="/marketing/pattern-builder" style={{ textDecoration: 'none' }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 2.25,
              py: 1.1,
              borderRadius: '10px',
              bgcolor: colors.gold,
              color: '#fff',
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '0.02em',
              boxShadow: `0 4px 14px ${colors.gold}35`,
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: colors.goldDark,
                transform: 'translateY(-1px)',
                boxShadow: `0 6px 18px ${colors.gold}50`,
              },
            }}
          >
            Open builder
            <TuneIcon sx={{ fontSize: 17 }} />
          </Box>
        </RouterLink>
      </Stack>
    </SectionCard>
  );
}

// =================================================================
// MAIN PAGE
// =================================================================
export function MarketingPage() {
  return (
    <SiteLayout subtitle="Marketing Intelligence" showDesktopHint>
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
        <MotionBox
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <VerifiedUserIcon sx={{ color: colors.gold, fontSize: 24 }} />
            <Typography
              sx={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontSize: { xs: 28, md: 38 },
                fontWeight: 700,
                color: colors.textPrimary,
                lineHeight: 1.1,
              }}
            >
              Marketing Intelligence
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 13.5, color: colors.textMuted, mb: 4 }}>
            HY whiskey content strategy, powered by studying 38 competitor and benchmark accounts.
            Every brief is derived from what wins in your peer set, translated into HY's voice.
          </Typography>
        </MotionBox>

        <Stack spacing={3}>
          {/* Tier 1 — the headline: what HY should post next */}
          <HeroBrief />

          {/* Tier 2 — the generator. Full width on its own row: it expands a
              lot once a brief is generated, so trapping it in a 2-col grid
              left the opposite column with a large empty gap. */}
          <ContentPatternMap />

          {/* Tier 3 — supporting analytics. Even 2-up grid; cards stretch to
              equal height so tops and bottoms line up. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
              gap: 3,
              alignItems: 'stretch',
            }}
          >
            <PatternBuilderLink />
            <IndustryNewsLink />
            <BlogLink />
            {FEATURE_COMPETITOR_WATCH ? <CompetitorLandscapeLink /> : null}
          </Box>
        </Stack>
      </Container>
    </SiteLayout>
  );
}
